// src/components/Training.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';

const Training = ({ user = {}, appConfig = {}, onBack }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [resultInfo, setResultInfo] = useState(null);
    const [lastFreeDate, setLastFreeDate] = useState('');

    const pointUnit = appConfig.pointUnitLabel || 'RB';
    const uid = user?.userId || user?.id;

    useEffect(() => {
        const fetchUserData = async () => {
            if (!uid) return;
            try {
                const docRef = doc(db, 'users', uid);
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    setLastFreeDate(snap.data().lastFreeTrainingDate || '');
                }
            } catch(e) {
                console.error(e);
            }
        };
        fetchUserData();
    }, [uid]);

    const today = new Date().toLocaleDateString('ja-JP');
    const isFree = lastFreeDate !== today;
    const cost = 50; // 特訓の消費コイン（変更可能です）

    const handleTrain = async () => {
        if (!uid) return toast.error("ユーザーエラー");

        if (!isFree && (user.points || 0) < cost) {
            return Swal.fire('エラー', `${pointUnit}が足りません！`, 'error');
        }

        if (!isFree) {
            const confirm = await Swal.fire({
                title: '特訓を開始しますか？',
                html: `<b>${cost} ${pointUnit}</b> を消費して激しい特訓を行います。<br>現在の所持: ${user.points || 0} ${pointUnit}`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: '特訓開始！',
                confirmButtonColor: '#e65100',
                cancelButtonText: 'やめる'
            });
            if (!confirm.isConfirmed) return;
        }

        setIsAnimating(true);
        setResultInfo(null);

        try {
            // ★ 抽選・無料判定・ポイント加算はすべて Cloud Functions で行う。
            //   以前はここで Math.random() していたため、
            //   コンソールから「超成功(+5)」を任意に発生させられた。
            const fns = getFunctions(getApp(), 'asia-northeast1');
            const res = await httpsCallable(fns, 'doTraining')({});
            const d = res?.data || {};

            const gainedPoints = d.gained ?? 1;
            const resultType = d.resultType || 'success';

            let resultText = '特訓成功！';
            let resultColor = '#4caf50';
            if (resultType === 'super') {
                resultText = '🌈 奇跡の超成功！！ 🌈';
                resultColor = '#d50000';
            } else if (resultType === 'great') {
                resultText = '🔥 大成功！！ 🔥';
                resultColor = '#ff9800';
            }

            // 演出用のタメ（2秒間）
            setTimeout(() => {
                setIsAnimating(false);
                setResultInfo({ type: resultType, text: resultText, points: gainedPoints, color: resultColor });
                // サーバーが返した日付で無料果を更新する
                if (d.isFree && d.today) setLastFreeDate(d.today);

                if (resultType === 'super') {
                    toast.success('とんでもない急成長を遂げた！！', { duration: 4000, icon: '🌟' });
                }
            }, 2000);

        } catch (e) {
            setIsAnimating(false);
            console.error('特訓エラー:', e?.code || '', e?.message || e);
            Swal.fire('エラー', e?.message || '特訓に失敗しました', 'error');
        }
    };

    return (
        <div className="container" style={{ minHeight: '100vh', background: '#111', paddingBottom: '50px', fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>
            <Toaster position="top-center" />
            <style>
                {`
                @keyframes intense-shake {
                    0% { transform: translate(2px, 1px) rotate(0deg) scale(1); }
                    10% { transform: translate(-1px, -2px) rotate(-2deg) scale(1.1); }
                    20% { transform: translate(-3px, 0px) rotate(2deg) scale(1.1); }
                    30% { transform: translate(3px, 2px) rotate(0deg) scale(1.2); }
                    40% { transform: translate(1px, -1px) rotate(2deg) scale(1.2); }
                    50% { transform: translate(-1px, 2px) rotate(-2deg) scale(1.3); }
                    60% { transform: translate(-3px, 1px) rotate(0deg) scale(1.3); }
                    70% { transform: translate(3px, 1px) rotate(-2deg) scale(1.2); }
                    80% { transform: translate(-1px, -1px) rotate(2deg) scale(1.1); }
                    90% { transform: translate(1px, 2px) rotate(0deg) scale(1.1); }
                    100% { transform: translate(1px, -2px) rotate(-2deg) scale(1); }
                }
                @keyframes pop-result {
                    0% { transform: scale(0.5); opacity: 0; }
                    70% { transform: scale(1.1); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .glow-text {
                    text-shadow: 0 0 10px rgba(255,255,255,0.8), 0 0 20px currentColor;
                }
                `}
            </style>

            {/* ヘッダー */}
            <div style={{ background: '#222', padding: '15px 20px', display: 'flex', alignItems: 'center', color: 'white', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'white', fontSize: '16px', cursor: 'pointer', padding: '0 10px 0 0' }}>← 戻る</button>
                <h2 style={{ margin: 0, fontSize: '18px', flex: 1, textAlign: 'center' }}>🔥 猛特訓 🔥</h2>
                <div style={{ width: '40px' }}></div>
            </div>

            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ 
                    background: 'radial-gradient(circle, #444 0%, #1a1a1a 100%)', borderRadius: '20px', padding: '40px 20px',
                    marginBottom: '30px', border: '2px solid #666', boxShadow: 'inset 0 0 30px rgba(0,0,0,0.9), 0 10px 20px rgba(0,0,0,0.5)'
                }}>
                    <div style={{ fontSize: '16px', color: '#ff9800', fontWeight: 'bold', marginBottom: '15px' }}>己の限界を超えろ！</div>
                    
                    <div style={{ fontSize: '14px', color: '#ccc', marginBottom: '30px' }}>
                        所持コイン: <span style={{color: '#ffeb3b', fontWeight: 'bold', fontSize: '20px'}}>{user?.points || 0}</span> {pointUnit}
                    </div>

                    <div style={{ 
                        fontSize: '90px', margin: '20px auto 40px', display: 'inline-block',
                        animation: isAnimating ? 'intense-shake 0.3s infinite' : 'none',
                        filter: isAnimating ? 'drop-shadow(0 0 30px #ff5252)' : 'drop-shadow(0 5px 10px rgba(0,0,0,0.8))',
                        transition: '0.3s'
                    }}>
                        {isAnimating ? '💦' : '🏋️'}
                    </div>

                    {!resultInfo ? (
                        <button 
                            onClick={handleTrain} disabled={isAnimating}
                            style={{ 
                                background: isAnimating ? '#555' : (isFree ? 'linear-gradient(135deg, #4caf50, #2e7d32)' : 'linear-gradient(135deg, #e65100, #d50000)'), 
                                color: 'white', border: 'none', padding: '20px', width: '100%', maxWidth: '300px', 
                                fontSize: '18px', fontWeight: 'bold', borderRadius: '50px', cursor: isAnimating ? 'not-allowed' : 'pointer',
                                boxShadow: isAnimating ? 'none' : '0 8px 20px rgba(0,0,0,0.5)',
                                transition: '0.2s', transform: isAnimating ? 'scale(0.95)' : 'scale(1)'
                            }}
                        >
                            {isAnimating ? '特訓中...' : (isFree ? '本日1回 無料で特訓！' : `猛特訓する (${cost} ${pointUnit})`)}
                        </button>
                    ) : (
                        <div style={{ animation: 'pop-result 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) both' }}>
                            <div className="glow-text" style={{ fontSize: '28px', fontWeight: '900', color: resultInfo.color, marginBottom: '15px', letterSpacing: '2px' }}>
                                {resultInfo.text}
                            </div>
                            <div style={{ background: 'rgba(0,0,0,0.6)', padding: '15px', borderRadius: '15px', display: 'inline-block', border: `1px solid ${resultInfo.color}` }}>
                                <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '5px' }}>獲得ステータスポイント</div>
                                <div style={{ fontSize: '40px', fontWeight: 'bold', color: 'white' }}>
                                    +{resultInfo.points} <span style={{fontSize:'16px', color:'#aaa'}}>P</span>
                                </div>
                            </div>
                            <div style={{ marginTop: '30px' }}>
                                <button 
                                    onClick={() => setResultInfo(null)} 
                                    style={{ background: 'transparent', border: '2px solid #888', color: '#aaa', padding: '10px 30px', borderRadius: '30px', fontSize: '14px', cursor: 'pointer' }}
                                >
                                    もう一度特訓する
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ルール説明 */}
                <div style={{ background: '#222', padding: '15px', borderRadius: '12px', textAlign: 'left', color: '#aaa', fontSize: '11px', lineHeight: '1.6' }}>
                    <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>💡 特訓のルール</div>
                    ・特訓を行うと、プロフィールの育成に使える「ステータスポイント」を獲得できます。<br/>
                    ・<span style={{color:'#4caf50'}}>通常成功(+1)</span>、<span style={{color:'#ff9800'}}>大成功(+3)</span>、稀に<span style={{color:'#d50000'}}>超成功(+5)</span>が発生します。<br/>
                    ・獲得したポイントは、マイページに戻ってステータスに割り振ってください。<br/>
                    <span style={{ color: '#4caf50', fontWeight: 'bold' }}>・毎日1回、無料で特訓が可能です！</span>
                </div>
            </div>
        </div>
    );
};

export default Training;